import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { CreateTemplateModal } from '../component/molecules/CreateTemplateModal'
import { PerformanceDetailsPane } from '../component/molecules/PerformanceDetailsPane'
import { PerformanceOverviewCards } from '../component/molecules/PerformanceOverviewCards'
import { PerformanceTemplateList } from '../component/molecules/PerformanceTemplateList'
import { useMyTeamStore } from '../store/my-team.store'
import { PerformanceReviewTemplate } from '../model/my-team-performance'
import { Box, Typography } from '@mui/material'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { Title1 } from 'src/feature/localization/component/atom/Title1'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { PerformanceInsightsCard } from '../component/molecules/PerformanceInsightsCard'

export const MyTeamPerformancePage = observer(() => {
  const myTeamStore = useMyTeamStore()
  const navigate = useNavigate()
  const templates = myTeamStore.performanceReviewTemplates
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const { translation } = useLocalizationStore()
  const performanceReviewTranslation = translation.pages.myTeam.performanceReview

  const selectedTemplate = templates.find(t => t.id === selectedId)

  useEffect(() => {
    myTeamStore.getAllPerformanceReviewTemplates()
  }, [myTeamStore])

  const handleUseTemplate = (template: PerformanceReviewTemplate) => {
    navigate(`${appRoutePath.myTeamPerformanceCreate}?from=${template.id}`)
  }

  const handleStartManually = () => {
    navigate(appRoutePath.myTeamPerformanceCreate)
  }

  return (
    <Box sx={styles.root}>
      <Title1 m={0} textValue={performanceReviewTranslation.title} />
      <Typography variant='body2' sx={styles.subtitle}>
        {performanceReviewTranslation.subtitle()}
      </Typography>
      <AppBox sx={styles.contentLayout}>
        <AppBox sx={styles.leftColumn}>
          <PerformanceOverviewCards
            templateCount={templates.length}
            onCreateClick={() => setShowCreateModal(true)}
          />

          <PerformanceTemplateList
            templates={templates}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </AppBox>

        <AppBox sx={styles.rightColumn}>
          {selectedId && (
            <PerformanceInsightsCard
              insights={myTeamStore.currentPerformanceReviewInsights}
            />
          )}
          <PerformanceDetailsPane
            templateId={selectedId}
            templateName={selectedTemplate?.name}
          />
        </AppBox>
      </AppBox>

      <CreateTemplateModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        templates={templates}
        onUseTemplate={handleUseTemplate}
        onStartManually={handleStartManually}
      />
    </Box>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    margin: { xs: '1rem', md: '1rem 5rem' },
    maxWidth: '100%',
  },
  contentLayout: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: {
      xs: '1fr',
      md: '1fr 1fr',
    },
    width: '100%',
  },
  leftColumn: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  rightColumn: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  subtitle: {
    color: 'text.secondary',
    marginTop: '-15px',
  },
} as const